import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { Project } from '../lib/types';

export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: () => api<{ projects: Project[] }>('/api/projects').then((r) => r.projects),
    refetchInterval: 30_000,
  });
}

export function useProject(slug: string | undefined) {
  return useQuery({
    queryKey: ['project', slug],
    queryFn: () => api<{ project: Project }>(`/api/projects/${slug}`).then((r) => r.project),
    enabled: Boolean(slug),
  });
}

export interface CreateProjectInput {
  slug: string;
  name: string;
  local_path: string;
  description?: string;
  repo_url?: string;
  default_branch?: string;
  /**
   * Static mode: when true, the panel auto-creates a minimal
   * `.pipeline/config.jsonc` in the project folder with the pipeline
   * configured to skip verifier/build/test commands. Use for projects
   * whose runtime is NOT installed on this server.
   */
  static_mode?: boolean;
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      api<{ project: Project }>('/api/projects', {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((r) => r.project),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export interface ProjectSettingsInput {
  planning_base_branch?: string;
  planning_concurrency?: number;
  /**
   * GitHub/Git repo URL. Set so the panel can render branch names as
   * compare links. Pass `null` (or empty string) to clear. Same shape
   * accepted by project create — SSH (`git@host:user/repo.git`) or
   * HTTPS URL.
   */
  repo_url?: string | null;
  /**
   * Default git branch — used as the base for GitHub compare URLs.
   * Typically `main` or `master`. Distinct from planning_base_branch
   * which is what the planner branches from.
   */
  default_branch?: string;
  /** Which pipeline binary runs this project's tasks. */
  pipeline_version?: 'v1' | 'v2';
  /** v2 execution mode: 'standard' (per-step) or 'phase-agentic'. */
  execution_mode?: 'standard' | 'phase-agentic';
  /** v2 git behavior: keep per-step commits or squash into one at the end. */
  commit_strategy?: 'per_step' | 'squash';
  /** v2: push the feature branch on success. */
  auto_push?: boolean;
  /** v2: remote to push to. */
  push_remote?: string;
  /** v2 capability: project can build/test (run the verifier). */
  full_setup?: boolean;
  /** v2 capability: project can run a browser smoke check. */
  browser_testing?: boolean;
}

/**
 * Admin-only settings update for the planning-pool architecture
 * (Phase 8). Restart of the panel worker is required for changes to
 * `planning_concurrency` to take effect on already-initialized pools.
 */
export function useUpdateProjectSettings(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProjectSettingsInput) =>
      api<{ project: Project; note?: string }>(`/api/projects/${slug}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['project', slug] });
    },
  });
}
